import { useEffect, useMemo } from "react";
import { createPath, matchPath, type To } from "react-router";
import { appRoutes } from "@/app/router";
import { useMaybeTabContext } from "@/components/layout/tab-context";
import { SmartViewKind } from "@/domains/smart/constants";
import { resolveSmartContextId } from "@/store/heads-up/SmartContextId";
import type { AppStore } from "@/store/AppStore";

export interface HeadsUpContextInfo {
  contextId: string | null;
  activeNoteId: string | null;
  spaceAccountId: string | null;
  isDatabaseReady: boolean;
}

function stripSearchAndHash(path: string): string {
  const queryIndex = path.indexOf("?");
  const hashIndex = path.indexOf("#");

  if (queryIndex < 0 && hashIndex < 0) {
    return path;
  }

  if (queryIndex < 0) {
    return path.slice(0, hashIndex);
  }

  if (hashIndex < 0) {
    return path.slice(0, queryIndex);
  }

  const firstSuffixIndex = Math.min(queryIndex, hashIndex);

  return path.slice(0, firstSuffixIndex);
}

function normalizePaneRoutePathname(tabPath: To): string {
  const path = typeof tabPath === "string" ? tabPath : createPath(tabPath);
  const hashRouteIndex = path.indexOf("#/");
  const routePath = hashRouteIndex >= 0 ? path.slice(hashRouteIndex + 1) : path;

  return stripSearchAndHash(routePath);
}

/**
 * Resolves the note Heads Up should target from the pane-local tab path when
 * the floating window is rendered inside a split-view pane.
 */
export function resolveHeadsUpActiveNoteId({
  fallbackNoteId,
  tabPath,
}: {
  fallbackNoteId: string | null;
  tabPath: To | null;
}): string | null {
  if (!tabPath) {
    return fallbackNoteId;
  }

  const pathname = normalizePaneRoutePathname(tabPath);

  const noteIdMatch = matchPath(
    appRoutes.notesView({ params: { noteId: ":noteId" }, starPattern: true }),
    pathname
  );

  return noteIdMatch?.params.noteId ?? null;
}

export function useContextId(store: AppStore): HeadsUpContextInfo {
  const tabContext = useMaybeTabContext();
  const activeNoteId = resolveHeadsUpActiveNoteId({
    fallbackNoteId: store.routing.noteIdParam ?? null,
    tabPath: tabContext?.activeTabPath ?? null,
  });
  const spaceAccountId = store.spaceAccounts.myPersonalSpaceAccountId ?? null;
  const isDatabaseReady = store.db.isInitialized();

  const contextId = useMemo(() => {
    return resolveSmartContextId({
      spaceAccountId,
      activeNoteId,
      smartViewKind: SmartViewKind.HeadsUp,
    });
  }, [spaceAccountId, activeNoteId]);

  useEffect(() => {
    store.headsUp.setActiveContextId(contextId);
    return () => store.headsUp.setActiveContextId(null);
  }, [contextId, store.headsUp]);

  return {
    contextId,
    activeNoteId,
    spaceAccountId,
    isDatabaseReady,
  };
}
